import { useState, useEffect, useCallback, useRef } from 'react';
import type { TemplateConfig } from '@zaviago/print-renderer';
import { DEFAULT_TEMPLATE_CONFIG } from '@zaviago/print-renderer';
import {
  getTemplateConfig,
  saveTemplateConfig,
  resetTemplateConfig,
} from '@/lib/print/printReactService';

export function useTemplateConfig(templateName: string | null) {
  const [config, setConfig] = useState<TemplateConfig>({ ...DEFAULT_TEMPLATE_CONFIG });
  const savedRef = useRef<string>(JSON.stringify(DEFAULT_TEMPLATE_CONFIG));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = JSON.stringify(config) !== savedRef.current;

  // Fetch config on mount / template change
  useEffect(() => {
    if (!templateName) return;
    let stale = false;
    setIsLoading(true);

    getTemplateConfig(templateName)
      .then((saved) => {
        if (stale) return;
        const merged = { ...DEFAULT_TEMPLATE_CONFIG, ...saved };
        setConfig(merged);
        savedRef.current = JSON.stringify(merged);
      })
      .catch(() => {
        if (!stale) {
          setConfig({ ...DEFAULT_TEMPLATE_CONFIG });
          savedRef.current = JSON.stringify(DEFAULT_TEMPLATE_CONFIG);
        }
      })
      .finally(() => {
        if (!stale) setIsLoading(false);
      });

    return () => { stale = true; };
  }, [templateName]);

  const updateConfig = useCallback((partial: Partial<TemplateConfig>) => {
    setConfig((prev) => ({ ...prev, ...partial }));
  }, []);

  const saveConfig = useCallback(async () => {
    if (!templateName) return;
    setIsSaving(true);
    try {
      await saveTemplateConfig(templateName, config);
      savedRef.current = JSON.stringify(config);
    } finally {
      setIsSaving(false);
    }
  }, [templateName, config]);

  const doResetConfig = useCallback(async () => {
    if (!templateName) return;
    setIsSaving(true);
    try {
      await resetTemplateConfig(templateName);
      const defaults = { ...DEFAULT_TEMPLATE_CONFIG };
      setConfig(defaults);
      savedRef.current = JSON.stringify(defaults);
    } finally {
      setIsSaving(false);
    }
  }, [templateName]);

  return {
    config,
    updateConfig,
    saveConfig,
    resetConfig: doResetConfig,
    isDirty,
    isLoading,
    isSaving,
  };
}
